import { HttpClient, HttpResponse } from '@angular/common/http';
import { isStringNullOrEmpty } from '@wz-utilities/string.utilities';
import * as CryptoJS from 'crypto-js';
import * as forge from 'node-forge';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpModels } from '../../../../../models/api/http.models';
import { ConfigurationModels } from '../../../../../models/configuration.models';
import { Adresse } from '../../components/Souscriptions/models/Adresse.model';
import { Guid_Mdp } from '../../components/Souscriptions/souscription-en-ligne/souscription-en-ligne.component';
export abstract class ApiServiceBase {
  private publicKey!: string;

  /**
   * Base class for Api Services
   * @param configService The configuration service
   * @param httpClient Angular Http Client
   * @param controllerName The controller name (ie: address)
   */
  constructor(
    protected config: ConfigurationModels.Configuration,
    protected httpClient: HttpClient,
    protected controllerName?: string,
  ) {}

  /**
   * Get a blob from an absolute path (azure storage), TunnelSante API is not involved here
   * @param url blob url
   * @param options options
   */
  protected getDirectBlob(
    url: string,
    options?: HttpModels.OptionOverrides,
  ): Observable<HttpModels.HttpFileResponse> {
    return this.httpClient
      .get(url, new HttpModels.GetBlobOptions({}, options))
      .pipe(
        map((res) => {
          let filename = this.getFileNameFromResponse(res);
          return new HttpModels.HttpFileResponse(filename, res.body!);
        }),
      );
  }

  protected getBlob(
    route: string,
    queryStringData?: { [key: string]: string | string[] },
    options?: HttpModels.OptionOverrides,
  ): Observable<HttpModels.HttpFileResponse> {
    return this.httpClient
      .get(
        this.resolveUrl(route),
        new HttpModels.GetBlobOptions(queryStringData!, options),
      )
      .pipe(
        map((res) => {
          let filename = this.getFileNameFromResponse(res);
          return new HttpModels.HttpFileResponse(filename, res.body!);
        }),
      );
  }

  protected getHttpResponse<T>(
    route: string,
    queryStringData?: { [key: string]: string | string[] },
    options?: HttpModels.OptionOverrides,
  ): Observable<HttpResponse<T>> {
    return this.httpClient.get<T>(
      this.resolveUrl(route),
      new HttpModels.GetJsonResponseOptions(queryStringData!, options),
    );
  }

  protected getJson<T>(
    route: string,
    queryStringData?: HttpModels.QueryStringData,
    options?: HttpModels.OptionOverrides,
  ): Observable<T> {
    return this.httpClient.get<T>(
      this.resolveUrl(route),
      new HttpModels.GetJsonOptions(queryStringData!, options),
    );
  }

  private buildFormatData(body: { [key: string]: any }) {
    let formData = new FormData();
    for (let key in body) {
      if (body.hasOwnProperty(key)) {
        let data = body[key];
        if (data instanceof Array) {
          data.forEach((item) => {
            if (item instanceof File) {
              formData.append(key, item, item.name);
            } else {
              formData.append(key, item != null ? String(item) : '');
            }
          });
        } else {
          formData.append(
            key,
            data instanceof File ? data : data != null ? String(data) : '',
          );
        }
      }
    }
    return formData;
  }

  protected putFormData<T>(
    route: string,
    body: { [key: string]: any | any[] },
    options?: HttpModels.OptionOverrides,
  ): Observable<T> {
    let formData = this.buildFormatData(body);
    return this.put<T>(route, formData, options);
  }

  protected patchFormData<T>(
    route: string,
    body: { [key: string]: any | any[] },
    options?: HttpModels.OptionOverrides,
  ): Observable<T> {
    let formData = this.buildFormatData(body);
    return this.patch<T>(route, formData, options);
  }

  protected postFormData<T>(
    route: string,
    body: { [key: string]: any | any[] },
    options?: HttpModels.OptionOverrides,
  ): Observable<T> {
    let formData = this.buildFormatData(body);
    return this.post<T>(route, formData, options);
  }

  protected setPublicKey(str: string) {
    this.publicKey = str;
  }

  protected getPublicKey() {
    if (!this.publicKey) {
      this.publicKey = `-----BEGIN PUBLIC KEY-----
            MIICIjANBgkqhkiG9w0BAQEFAAOCAg8AMIICCgKCAgEAwf7LAncOJjSt12SoUpFJ
            gm9A1qww5s2M46yGByxONgf8HShAraqJlgkeuYTzIcRsNlmAnZtg7Yz1JdrN5nrt
            CCDww5hnbmCE42psJSdCsjj7QoxfvGagdalacmjqooNhSn0LgRegBUA4qvU5MZm6
            T6D6I2JoNb3FinSia24y8ni9H2tSH+FFlJ2qDXY9quuDXajGpMxjAlCQ99/h52VJ
            3+cjymaODQlc27VY8xRzfIfzkASOj+O5Pqe07zOZxrPOvrfI6gfKSlCslgZAGAlV
            XXdpTjWJqbu2xZgpHh6braJJtcflUaurdC8fVgVZjYT+QRwQh7ADnMSH15adgH44
            dZj2S8GMUatzmvhZYlUn708YQ+8DnUHL5huAN9/0wL3huLp06AqMzP/DFUrF0afC
            kfZWzN/vKcGDlMSY/yxM4iRMYXRekSjraKbm1TUUv3Ol1d7pEVrKPgAVcwUFFClm
            Qa0Xq3cCiRra8ZRrkeeFI8tL2b9nZBJJoSfB0lK4gxthIUgqEtGR6tpfqfGJQ/2s
            EHzdwtCUn97gRA9/e6C+hvfQqgIA5DwI6eJ8S1VkXX2+tM/qpNRVGVviKLlW0qK+
            ZzY1jEEHVm39KtjHDqGmLCTOFiO8ACHhWBYxoHOpuoeh/6nQYD5V8UBNMLZD1TEv
            Da/x0rghpsFrcb2IIwTbU6MCAwEAAQ==
            -----END PUBLIC KEY-----`;
    }
    return this.publicKey;
  }

  generateKey(ndrChar: number): string {
    let text = '';
    let possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890';
    let lengthOfCode = ndrChar;
    for (let i = 0; i < lengthOfCode; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  encryptKey(key: string): string {
    let rsa = forge.pki.publicKeyFromPem(this.getPublicKey());
    return window.btoa(rsa.encrypt(key));
  }

  encryptData(payload: any): DataCrypted {
    let rsa = forge.pki.publicKeyFromPem(this.getPublicKey());
    let stringPayload = JSON.stringify(payload);

    let keyString = this.generateKey(16);
    let ivString = this.generateKey(16);

    let key = CryptoJS.enc.Utf8.parse(keyString);
    let iv = CryptoJS.enc.Utf8.parse(ivString);

    let keyBase64 = CryptoJS.enc.Base64.stringify(key);
    let ivBase64 = CryptoJS.enc.Base64.stringify(iv);

    let keyEncrypted = window.btoa(rsa.encrypt(keyBase64));
    let ivEncrypted = window.btoa(rsa.encrypt(ivBase64));

    let data = CryptoJS.AES.encrypt(
      CryptoJS.enc.Utf8.parse(stringPayload),
      key,
      {
        keySize: 128 / 8,
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    return {
      data: CryptoJS.enc.Base64.stringify(data.ciphertext),
      key: keyEncrypted,
      iv: ivEncrypted,
    };
  }

  protected postWithBlobResponse(
    route: string,
    body: { [key: string]: any | any[] },
    options?: HttpModels.OptionOverrides,
  ): Observable<HttpModels.HttpFileResponse> {
    return this.httpClient
      .post(
        this.resolveUrl(route),
        body,
        new HttpModels.GetBlobOptions({}, options),
      )
      .pipe(
        map((res) => {
          let filename = this.getFileNameFromResponse(res);
          return new HttpModels.HttpFileResponse(filename, res.body!);
        }),
      );
  }

  protected post<T>(
    route: string,
    body: any,
    options?: HttpModels.OptionOverrides,
  ): Observable<T> {
    return this.httpClient.post<T>(
      this.resolveUrl(route),
      body,
      new HttpModels.GetJsonOptions({}, options),
    );
  }

  protected getXml(route: string, body: any): Observable<any> {
    return this.httpClient.post(this.resolveUrl(route), body, {
      responseType: 'text',
    });
  }

  protected delete<T>(
    route: string,
    options?: HttpModels.OptionOverrides,
  ): Observable<T> {
    return this.httpClient.delete<T>(
      this.resolveUrl(route),
      new HttpModels.GetJsonOptions({}, options),
    );
  }

  protected put<T>(
    route: string,
    body: any,
    options?: HttpModels.OptionOverrides,
  ): Observable<T> {
    return this.httpClient.put<T>(
      this.resolveUrl(route),
      body,
      new HttpModels.GetJsonOptions({}, options),
    );
  }

  protected patch<T>(
    route: string,
    body: any,
    options?: HttpModels.OptionOverrides,
  ): Observable<T> {
    return this.httpClient.patch<T>(
      this.resolveUrl(route),
      body,
      new HttpModels.GetJsonOptions({}, options),
    );
  }

  private resolveUrl(route: string): string {
    if (this.controllerName) {
      return `${this.config.apiRootUrl}/${this.controllerName}${route}`;
    }

    return `${this.config.apiRootUrl}${route}`;
  }

  private getFileNameFromResponse(response: HttpResponse<any>): string {
    if (!response.headers || !response.headers.get('Content-Disposition')) {
      return '';
    }

    const contentDispositionHeader = response.headers.get(
      'Content-Disposition',
    );
    const splitted = contentDispositionHeader?.split(';');
    let fileNameInfo = splitted?.find((s: any) => {
      if (s.toLocaleLowerCase().indexOf('filename') !== -1) {
        return true;
      }

      return false;
    });

    if (isStringNullOrEmpty(fileNameInfo!)) {
      return '';
    }

    // trim, then split to get the filename (right part), then remove quotes
    return fileNameInfo
      ? fileNameInfo.trim().split('=')[1].replace(/"/g, '')
      : '';
  }

  getStudent(route: string): Observable<Student[]> {
    return this.httpClient.get<Student[]>(this.resolveUrl(route));
  }

  ///SOUSCRIPTION EN LIGNE ANGULAR:
  getEtape(route: string): Observable<any> {
    return this.httpClient.get<any>(this.resolveUrl(route));
  }

  getMtxLogin(route: string): Observable<MtxLoginResponse> {
    return this.httpClient.get<MtxLoginResponse>(this.resolveUrl(route));
  }

  letsEncryptDataClient(route: any, token: string, data: any) {
    return this.httpClient.post(
      this.resolveUrl(route),
      { token: token, data: JSON.stringify(data) },
      { responseType: 'json' },
    );
  }

  letsDecryptDataClient(
    route: any,
    token: string,
    encrypted: string,
  ): Observable<any> {
    return this.httpClient.post<any>(
      this.resolveUrl(route),
      { token: token, data: encrypted },
      { responseType: 'json' },
    );
  }

  getNpsQuestions(route: string): Observable<NpsQuestions[]> {
    return this.httpClient.get<NpsQuestions[]>(this.resolveUrl(route));
  }

  getNpsQuestionsByType(route: string): Observable<NpsQuestions[]> {
    return this.httpClient.get<NpsQuestions[]>(this.resolveUrl(route));
  }
  getFicheAdhesionGuid(route: string): Observable<Guid_Mdp[]> {
    return this.httpClient.get<Guid_Mdp[]>(this.resolveUrl(route));
  }
  getFicheAdhesionGuidInstance(route: string): Observable<Guid_Mdp[]> {
    return this.httpClient.get<Guid_Mdp[]>(this.resolveUrl(route));
  }
  getSiteSouscription(route: string): Observable<any> {
    return this.httpClient.get<any>(this.resolveUrl(route));
  }
  getSiteSouscriptionInstance(route: string): Observable<Guid_Mdp[]> {
    return this.httpClient.get<Guid_Mdp[]>(this.resolveUrl(route));
  }
  httpGetFicheCompementaireSante(route: string): Observable<Guid_Mdp[]> {
    return this.httpClient.get<Guid_Mdp[]>(this.resolveUrl(route));
  }
  getFicheSubscribe(route: string): Observable<Guid_Mdp[]> {
    return this.httpClient.get<Guid_Mdp[]>(this.resolveUrl(route));
  }
  getAdhesionPaiement(route: string): Observable<Guid_Mdp[]> {
    return this.httpClient.get<Guid_Mdp[]>(this.resolveUrl(route));
  }
  getAdhesionPaiementInstance(route: string): Observable<Guid_Mdp[]> {
    return this.httpClient.get<Guid_Mdp[]>(this.resolveUrl(route));
  }
  getAdhesionTarif(route: string): Observable<Guid_Mdp[]> {
    return this.httpClient.get<Guid_Mdp[]>(this.resolveUrl(route));
  }
  httpGetAdhesionTarifInstance(route: string): Observable<Guid_Mdp[]> {
    return this.httpClient.get<any>(this.resolveUrl(route));
  }
  getAdhesionPDF(route: string): Observable<Guid_Mdp[]> {
    return this.httpClient.get<Guid_Mdp[]>(this.resolveUrl(route));
  }
  getListVillesCodePostales(route: string): Observable<Adresse[]> {
    return this.httpClient.get<Adresse[]>(this.resolveUrl(route));
  }
  getListVillesByCodePostales(route: string): Observable<Adresse[]> {
    return this.httpClient.get<Adresse[]>(this.resolveUrl(route));
  }
  getAdhesionDocSignature(route: string): Observable<any[]> {
    return this.httpClient.get<any[]>(this.resolveUrl(route));
  }
  getDocFraisGestion(route: string): Observable<any> {
    return this.httpClient.get<any>(this.resolveUrl(route));
  }

  getDetailsCotis(route: string): Observable<any> {
    return this.httpClient.get<any>(this.resolveUrl(route));
  }
}

interface Student {
  id: number;
  name: string;
  roll: number;
}
interface DataCrypted {
  data: string;
  key: string;
  iv: string;
}
export interface NpsQuestions {
  id: number;
  questionLabel: string;
  questionType: string;
}

export interface MtxLoginPayload {
  email: string;
  password: string;
}
export interface MtxLoginResponse {
  success: boolean;
  message?: string;
  data?: MtxDataLoginResponse;
}

export interface MtxDataLoginResponse {
  token: string;
  expiration: string;
}

export interface MtxEncryptResponse {
  success: boolean;
  message?: string;
  request?: MtxDataEncryptResponse;
}

export interface MtxDataEncryptResponse {
  encrypt: string;
}

export interface MtxDecryptPayload {
  encrypted: string;
}

export interface MtxDecryptResponse {
  success: boolean;
  message?: string;
  request?: any;
}

export interface NpsReponse {
  ID: number;
  questionsID: string; // de format "1,2,3"
  message: string;
  leadid: number;
  reponseDate: Date;
  type: string;
}
